import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { getShapeById } from './shape-list'

export function expandGroupDescendants(rootIds: string[], shapes: ShapeData[]): string[] {
  const expanded = new Set(rootIds)
  let changed = true

  while (changed) {
    changed = false

    for (const shape of shapes) {
      if (shape.parentGroupId && expanded.has(shape.parentGroupId) && !expanded.has(shape.id)) {
        expanded.add(shape.id)
        changed = true
      }
    }
  }

  return Array.from(expanded)
}

export function getTopSelectableAncestorId(shapeId: string, shapes: ShapeData[]): string | null {
  let current = getShapeById(shapes, shapeId)

  if (!current) return null

  while (current?.parentGroupId) {
    const parent = getShapeById(shapes, current.parentGroupId)

    if (!parent) break
    current = parent
  }

  return current?.id ?? null
}

export function toSelectableRootIds(ids: string[], shapes: ShapeData[]): string[] {
  const seen = new Set<string>()
  const next: string[] = []

  for (const id of ids) {
    const rootId = getTopSelectableAncestorId(id, shapes)

    if (!rootId || seen.has(rootId)) continue
    const root = getShapeById(shapes, rootId)

    if (!root || !isShapeSelectable(root, null)) continue
    seen.add(rootId)
    next.push(rootId)
  }

  return next
}

/** Shapes on hidden layers are excluded from selection. */
export function isShapeSelectable(shape: ShapeData, editScopeGroupId?: string | null): boolean {
  if (shape.hidden === true) return false
  if (editScopeGroupId) return shape.parentGroupId === editScopeGroupId
  return !shape.parentGroupId
}

export function applySelectionLockPolicy(
  ids: string[],
  shapes: ShapeData[],
  opts?: { preserveAllLockedMultiSelection?: boolean }
): string[] {
  if (ids.length <= 1) return ids
  const lockedIdSet = new Set(
    shapes.filter((shape) => shape.locked === true).map((shape) => shape.id)
  )

  if (lockedIdSet.size === 0) return ids
  const allLocked = ids.every((id) => lockedIdSet.has(id))

  if (allLocked && opts?.preserveAllLockedMultiSelection) return ids
  return ids.filter((id) => !lockedIdSet.has(id))
}

export function areIdArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false
  }

  return true
}

export function normalizeSelectableIdsForScope(
  ids: string[],
  shapes: ShapeData[],
  editScopeGroupId?: string | null
): string[] {
  if (!editScopeGroupId) {
    return toSelectableRootIds(ids, shapes)
  }

  const seen = new Set<string>()
  const next: string[] = []

  for (const id of ids) {
    const shape = getShapeById(shapes, id)

    if (!shape || !isShapeSelectable(shape, editScopeGroupId) || seen.has(shape.id)) continue
    seen.add(shape.id)
    next.push(shape.id)
  }

  return next
}

export function shapeSelectionSetsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sa = [...a].sort()
  const sb = [...b].sort()

  return sa.every((id, i) => id === sb[i])
}
